import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ArrowRight, EnvelopeSimple, Lock } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { useAuth } from "../../contexts/AuthContext";
import { isAdminRole } from "../../utils/roles";

const ADMIN_REMEMBER_EMAIL_KEY = "df_admin_remember_email";

function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function AdminLoginScreen() {
  const { user, signIn, signOut } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // On mount, load remembered admin email if exists
  useEffect(() => {
    const rememberedEmail = localStorage.getItem(ADMIN_REMEMBER_EMAIL_KEY);
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  // If already authenticated as admin, redirect to admin dashboard
  useEffect(() => {
    if (user && isAdminRole(user.role)) {
      navigate("/admin", { replace: true });
    }
  }, [user, navigate]);

  // Handle remember me changes
  const handleRememberMeChange = (checked) => {
    setRememberMe(checked);
    if (!checked) {
      localStorage.removeItem(ADMIN_REMEMBER_EMAIL_KEY);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const trimmedEmail = email.trim();
      const data = await signIn(trimmedEmail, password);
      
      // Check if user is admin
      if (!isAdminRole(data?.role)) {
        setError("This account does not have administrator access.");
        // Sign out the non-admin user to prevent backdoor session
        await signOut();
        setLoading(false);
        return;
      }
      
      // Save email if remember me is checked
      if (rememberMe) {
        localStorage.setItem(ADMIN_REMEMBER_EMAIL_KEY, trimmedEmail);
      }
      
      // Navigate to admin dashboard
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  const canSubmit = email.trim().length > 3 && password.length > 0 && !loading;

  // Show spinner while checking auth state
  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4"
      data-testid="admin-login-screen"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-200/40 via-white to-slate-50" />

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white border border-slate-200 p-8 shadow-lg rounded-lg">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4 p-3 bg-slate-100 border border-slate-200 rounded-lg">
              <ShieldCheck size={48} weight="duotone" className="text-slate-700" />
            </div>
            <h1 className="font-cabinet text-2xl font-black tracking-tight text-slate-900 mb-2">
              ADMIN PORTAL
            </h1>
            <h2 className="font-cabinet text-lg font-bold tracking-tight text-slate-600">
              Administrator Sign-in
            </h2>
            <p className="text-sm text-slate-500 mt-3 font-sans leading-relaxed">
              Admin access only. Accounts are provisioned by an administrator.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="relative">
              <EnvelopeSimple
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <Input
                type="email"
                placeholder="Administrator email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-12 rounded-sm border-slate-300 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-slate-500 focus-visible:border-slate-500 font-mono text-sm"
                data-testid="admin-login-email-input"
                autoFocus
                required
              />
            </div>

            <div className="relative">
              <Lock
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 h-12 rounded-sm border-slate-300 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-slate-500 focus-visible:border-slate-500 font-mono text-sm"
                data-testid="admin-login-password-input"
                required
              />
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center space-x-2 pt-1">
              <Checkbox
                id="admin-remember-me"
                checked={rememberMe}
                onCheckedChange={handleRememberMeChange}
                data-testid="admin-remember-me-checkbox"
              />
              <Label
                htmlFor="admin-remember-me"
                className="text-sm text-slate-600 cursor-pointer font-medium"
              >
                Remember me
              </Label>
            </div>

            {error && (
              <div
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm font-medium rounded-sm"
                data-testid="admin-login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={!canSubmit}
              className="w-full h-12 rounded-sm bg-slate-800 hover:bg-slate-700 text-white font-semibold tracking-wide uppercase text-xs border border-slate-800"
              data-testid="admin-login-submit-btn"
            >
              {loading ? (
                <div className="spinner" />
              ) : (
                <>
                  Sign In to Admin Portal
                  <ArrowRight size={16} className="ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Footer - No signup, just informational */}
          <div className="mt-8 pt-6 border-t border-slate-200">
            <p className="text-xs text-slate-500 text-center leading-relaxed">
              This portal is for system administrators only. If you need access, please contact your organization&apos;s admin.
            </p>
          </div>
        </div>

        {/* Security notice */}
        <p className="text-xs text-slate-400 text-center mt-4">
          Access to this portal is monitored and restricted.
        </p>
      </div>
    </div>
  );
}
